import ChatMessage from '@/Components/Common/Markdown/ChatMessage';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { processMessage, ReferenceDiv, clearStableDummyIdCache } from './utils';
import useChatStore, { COLUMNS } from 'Components/Chat/store';
import { jsPlumb } from 'jsplumb';
import useThemeStore from 'Theme/store';
import useSmartMergeStore from 'Features/SmartMerge/store';
import { SelectionChangeWarning } from 'Features/Core/Components/SmartMerge/SmartMergeDialog';
import { ConversationPathWarning } from 'Features/Core/Components/TreeView/TreeView';
const ListingChatMessages = ({
  messages = [],
  avatar,
  loading,
  xchatId,
  optimizing,
  messageRefs,
  currentEvent,
  onSend,
  changeOutput,
  onRegenerate,
  handleAddModels,
  onRegenerateEdit,
  setDialogContent,
  continueResponse,
  setShowImageDialog,
  handleScriptForTTS,
  handleMessageDelete,
  handleResponseDelete,
  calculateCoinsEdit,
  handleScrollRequest,
  streamingMessage,
  redrawTrigger = 0,
}) => {
  const handleOpenFull = (content) => {
    setDialogContent(content);
    setShowImageDialog(true);
  };

  const renderType = useChatStore((state) => state.renderType);
  const { theme: themeValue } = useThemeStore();
  const previousTheme = useRef(themeValue);

  // Tree-view refs and state
  const jsPlumbInstance = useRef(null);
  const plumbContainerRef = useRef(null);
  const elementRefs = useRef({});
  const currentConnections = useRef({});
  const connectionsByInteraction = useRef({});
  const previousSelections = useRef({});
  const streamingConnection = useRef(null);

  // Communication signals between components
  const [ringClickSignal, setRingClickSignal] = useState(null);
  const [dotReorderSignal, setDotReorderSignal] = useState(null);
  const [reorderedItems, setReorderedItems] = useState({});

  // Selection change warning modal state
  const [selectionChangeWarning, setSelectionChangeWarning] = useState({
    open: false,
    pendingSelection: null,
  });

  // Conversation path warning modal state
  const [conversationPathWarning, setConversationPathWarning] = useState({
    open: false,
    pendingSelection: null,
  });

  const handleCloseSelectionWarning = () => {
    setSelectionChangeWarning({
      open: false,
      pendingSelection: null,
    });
  };

  const handleConfirmSelectionWarning = async () => {
    const { messageIndex, responseIndex } =
      selectionChangeWarning.pendingSelection;

    // Close SmartMerge first
    useSmartMergeStore.getState().setActive(false);

    // Reset reordering when changing model selection to prevent dot/card mismatch
    if (reorderedItems[messageIndex]) {
      setReorderedItems((prev) => {
        const newReorderedItems = { ...prev };
        delete newReorderedItems[messageIndex];
        return newReorderedItems;
      });

      setDotReorderSignal({
        userMessageIndex: messageIndex,
        newOrder: null,
        timestamp: Date.now(),
      });
    }

    const aiResponsesIndex = messageIndex - 1;
    if (aiResponsesIndex >= 0 && Array.isArray(messages[aiResponsesIndex])) {
      if (messages[aiResponsesIndex]?.[responseIndex]) {
        changeOutput(messages[aiResponsesIndex][responseIndex]);
      }
    }

    handleCloseSelectionWarning();
  };

  const handleCloseConversationPathWarning = () => {
    setConversationPathWarning({
      open: false,
      pendingSelection: null,
    });
  };

  const handleConfirmConversationPathWarning = async () => {
    const { messageIndex, responseIndex } =
      conversationPathWarning.pendingSelection;

    // Reset reordering when changing model selection to prevent dot/card mismatch
    if (reorderedItems[messageIndex]) {
      setReorderedItems((prev) => {
        const newReorderedItems = { ...prev };
        delete newReorderedItems[messageIndex];
        return newReorderedItems;
      });

      setDotReorderSignal({
        userMessageIndex: messageIndex,
        newOrder: null,
        timestamp: Date.now(),
      });
    }

    const aiResponsesIndex = messageIndex - 1;
    if (aiResponsesIndex >= 0 && Array.isArray(messages[aiResponsesIndex])) {
      if (messages[aiResponsesIndex]?.[responseIndex]) {
        changeOutput(messages[aiResponsesIndex][responseIndex]);
      }
    }

    handleCloseConversationPathWarning();
  };

  // Helper function to determine if a message index is the most recent user message
  const isMostRecentUserMessage = useCallback(
    (messageIndex) => {
      const mostRecentUserMessageIndex = messages?.findIndex((msg, idx) => {
        const { isUserMessage } = processMessage(msg);
        return isUserMessage;
      });
      return messageIndex === mostRecentUserMessageIndex;
    },
    [messages]
  );

  const registerElementRef = useCallback((id, node) => {
    if (node) {
      elementRefs.current[id] = node;
    } else {
      delete elementRefs.current[id];
    }
  }, []);

  // Update positions of dynamic connection points (virtual merge points and merge sources)
  const updateDynamicPointPositions = useCallback(() => {
    if (
      !plumbContainerRef.current ||
      !jsPlumbInstance.current ||
      !messages?.length
    ) {
      return;
    }

    const refs = elementRefs.current;

    Object.keys(refs).forEach((key) => {
      if (key.startsWith('virtual-merge-point-')) {
        updateVirtualMergePointPosition(key, refs);
      } else if (key.match(/^merge-source-.+-msg-\d+$/)) {
        updateMergeSourcePointPosition(key, refs);
      }
    });

    if (jsPlumbInstance.current) {
      jsPlumbInstance.current.repaintEverything();
    }
  }, [messages]);

  const updateVirtualMergePointPosition = (key, refs) => {
    const virtualPoint = refs[key];
    const mergeCardId = key.replace('virtual-merge-point-', 'merge-card-');
    const mergeCardNode = refs[mergeCardId];
    const messageIndex = parseInt(
      virtualPoint.getAttribute('data-message-index'),
      10
    );
    const responseId = virtualPoint.getAttribute('data-response-id');

    if (!virtualPoint || !mergeCardNode || isNaN(messageIndex) || !responseId) {
      return;
    }

    const aiResponsesIndex = messageIndex - 1;
    if (aiResponsesIndex < 0) return;

    let subsequentResponses = [];
    if (Array.isArray(messages[aiResponsesIndex])) {
      subsequentResponses = messages[aiResponsesIndex];
    } else if (messages[aiResponsesIndex]?.data?.role === 'assistant') {
      subsequentResponses = [messages[aiResponsesIndex]];
    }

    if (!subsequentResponses.length) return;

    const selectedResponse = subsequentResponses.find(
      (r) => r._id === responseId
    );
    const sourceModels =
      selectedResponse && Array.isArray(selectedResponse.model)
        ? selectedResponse.model
        : [];

    if (sourceModels.length > 0) {
      const targetRect = mergeCardNode.getBoundingClientRect();
      const containerRect = plumbContainerRef.current.getBoundingClientRect();

      const sourceCardPositions = subsequentResponses
        .filter((resp) => !resp.merged && sourceModels.includes(resp.model))
        .map((resp) => {
          const sourceCardNode = refs[`card-${resp._id}`];
          if (sourceCardNode) {
            const sourceRect = sourceCardNode.getBoundingClientRect();
            return {
              left: sourceRect.left + sourceRect.width / 2,
              bottom: sourceRect.bottom,
            };
          }
          return null;
        })
        .filter(Boolean);

      if (sourceCardPositions.length > 0) {
        const avgLeft =
          sourceCardPositions.reduce((sum, pos) => sum + pos.left, 0) /
          sourceCardPositions.length;
        const maxBottom = Math.max(
          ...sourceCardPositions.map((pos) => pos.bottom)
        );
        const mergeCardTop = targetRect.top - containerRect.top;
        const distance = mergeCardTop - (maxBottom - containerRect.top);
        const virtualPointTop = mergeCardTop - distance * 0.45;

        virtualPoint.style.top = `${virtualPointTop}px`;
        virtualPoint.style.left = `${avgLeft - containerRect.left - 10}px`;
      }
    }
  };

  const updateMergeSourcePointPosition = (key, refs) => {
    const mergeSourcePoint = refs[key];
    const parts = key.split('-');
    const mergeId = parts[2];
    const mergeCardNode = refs[`merge-card-${mergeId}`];

    if (mergeSourcePoint && mergeCardNode) {
      const targetRect = mergeCardNode.getBoundingClientRect();
      const containerRect = plumbContainerRef.current.getBoundingClientRect();

      mergeSourcePoint.style.top = `${
        targetRect.bottom - containerRect.top - 2
      }px`;
      mergeSourcePoint.style.left = `${
        targetRect.left + targetRect.width / 2 - containerRect.left - 2
      }px`;
    }
  };

  const updateDynamicPointPositionsRef = useRef(updateDynamicPointPositions);

  useEffect(() => {
    updateDynamicPointPositionsRef.current = updateDynamicPointPositions;
  }, [updateDynamicPointPositions]);

  // Initialize jsPlumb instance for Tree-view connections
  useEffect(() => {
    if (renderType === COLUMNS && plumbContainerRef.current) {
      if (!jsPlumbInstance.current) {
        jsPlumbInstance.current = jsPlumb.getInstance({
          Container: plumbContainerRef.current,
        });
        jsPlumbInstance.current.importDefaults({
          ConnectionsDetachable: false,
          Connector: [
            'Flowchart',
            { cornerRadius: 40, stub: [10, 10], gap: 0 },
          ],
          Endpoint: 'Blank',
          EndpointStyle: { radius: 0 },
          PaintStyle: { stroke: '#4F46E5', strokeWidth: 2 },
          Anchors: ['Bottom', 'Top'],
        });
      }

      return () => {
        if (jsPlumbInstance.current) {
          jsPlumbInstance.current.reset();
          jsPlumbInstance.current = null;
        }
        cleanupConnectionRefs();
      };
    } else {
      if (jsPlumbInstance.current) {
        jsPlumbInstance.current.reset();
        jsPlumbInstance.current = null;
        cleanupConnectionRefs();
      }
    }
  }, [renderType, xchatId]);

  useEffect(() => clearStableDummyIdCache(), [xchatId]);

  const cleanupConnectionRefs = useCallback(() => {
    currentConnections.current = {};
    connectionsByInteraction.current = {};
    previousSelections.current = {};

    Object.keys(elementRefs.current).forEach((key) => {
      const elementNode = elementRefs.current[key];
      if (
        elementNode &&
        (key.startsWith('virtual-merge-point-') ||
          key.match(/^merge-source-.+-msg-\d+$/))
      ) {
        elementNode.parentNode?.removeChild(elementNode);
        delete elementRefs.current[key];
      }
    });
  }, []);

  // Setup resize listeners for dynamic positioning
  useEffect(() => {
    let observerInstance = null;

    const handleResize = () => {
      if (updateDynamicPointPositionsRef.current) {
        updateDynamicPointPositionsRef.current();
      }
    };

    if (
      renderType === COLUMNS &&
      plumbContainerRef.current &&
      jsPlumbInstance.current
    ) {
      observerInstance = new ResizeObserver(handleResize);
      observerInstance.observe(plumbContainerRef.current);
      window.addEventListener('resize', handleResize);

      return () => {
        if (observerInstance && plumbContainerRef.current) {
          observerInstance.unobserve(plumbContainerRef.current);
        }
        window.removeEventListener('resize', handleResize);
      };
    }

    return () => {
      if (observerInstance && plumbContainerRef.current) {
        observerInstance.unobserve(plumbContainerRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [renderType]);

  useEffect(() => {
    const instance = jsPlumbInstance.current;
    if (!instance) return;

    if (renderType !== COLUMNS || loading) {
      performFullCleanup(instance);
      return;
    }

    const repaintDebounced = debounce(() => {
      jsPlumbInstance.current?.repaintEverything();
    }, 50);

    const timeoutId = setTimeout(() => {
      if (jsPlumbInstance.current) {
        updateConnections(jsPlumbInstance.current, elementRefs.current);
        repaintDebounced();
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [messages, renderType, loading, updateDynamicPointPositions, themeValue]);

  useEffect(() => {
    const instance = jsPlumbInstance.current;
    if (!instance || renderType !== COLUMNS) return;

    if (loading && streamingMessage) {
      handleStreamingStart(instance);
    } else if (!loading && streamingConnection.current) {
      try {
        instance.deleteConnection(streamingConnection.current.connection);
      } catch (error) {}
      streamingConnection.current = null;
    }
  }, [loading, streamingMessage, renderType]);

  useEffect(() => {
    const instance = jsPlumbInstance.current;
    const isPostStreamingUpdate =
      !loading &&
      !streamingMessage &&
      instance &&
      renderType === COLUMNS &&
      messages?.length > 0 &&
      Object.keys(previousSelections.current).length > 0;

    if (isPostStreamingUpdate) {
      setTimeout(() => redrawNewMessageConnections(instance), 100);
    }
  }, [messages?.length, loading, streamingMessage, renderType]);

  const handleStreamingStart = (instance) => {
    createStreamingConnection(instance);
    redrawPreviousInteractions(instance, 4);
  };

  const redrawNewMessageConnections = useCallback(
    (instance) => {
      Object.keys(currentConnections.current).forEach((connKey) => {
        const connection = currentConnections.current[connKey];
        if (connection) {
          try {
            instance.deleteConnection(connection);
          } catch (e) {}
          delete currentConnections.current[connKey];
        }
      });

      Object.keys(elementRefs.current).forEach((key) => {
        const elementNode = elementRefs.current[key];
        if (
          elementNode &&
          (key.startsWith('virtual-merge-point-') ||
            key.match(/^merge-source-.+-msg-\d+$/))
        ) {
          try {
            instance.remove(elementNode);
          } catch (err) {}
          elementNode.parentNode?.removeChild(elementNode);
          delete elementRefs.current[key];
        }
      });

      const newConnectionsByInteractionState = {
        ...connectionsByInteraction.current,
      };

      instance.batch(() => {
        messages.forEach((item, userMessageIndex) => {
          const { realKey: interactionKey, isUserMessage } =
            processMessage(item);
          if (!isUserMessage) return;

          const aiResponsesIndex = userMessageIndex - 1;
          const subsequentResponses = getSubsequentResponses(aiResponsesIndex);

          const currentSelectedResponse =
            subsequentResponses.find((resp) => resp.selected === true) ||
            (subsequentResponses.length === 1 ? subsequentResponses[0] : null);

          // Extract reordered item IDs for this user message
          const reorderedItemIds = reorderedItems[userMessageIndex] || null;

          if (currentSelectedResponse) {
            const connectionKeys = currentSelectedResponse.merged
              ? drawMergeConnections(
                  instance,
                  elementRefs.current,
                  currentSelectedResponse,
                  subsequentResponses,
                  userMessageIndex,
                  aiResponsesIndex,
                  true,
                  reorderedItemIds
                )
              : drawNormalConnections(
                  instance,
                  elementRefs.current,
                  currentSelectedResponse,
                  subsequentResponses,
                  userMessageIndex,
                  aiResponsesIndex,
                  reorderedItemIds
                );

            newConnectionsByInteractionState[interactionKey] = connectionKeys;

            const newSelectionData = {
              id: currentSelectedResponse._id,
              merged: currentSelectedResponse.merged,
              models: currentSelectedResponse.model,
            };

            previousSelections.current[interactionKey] = newSelectionData;
          }

          // Draw grey connections for non-selected responses
          const nonSelectedResponses = subsequentResponses.filter((resp) => {
            // Skip merged responses
            if (resp.merged) return false;

            // Skip the currently selected response
            if (
              currentSelectedResponse &&
              resp._id === currentSelectedResponse._id
            )
              return false;

            // Skip responses that are sources for a selected merge (they already get purple arrows)
            if (
              currentSelectedResponse?.merged &&
              Array.isArray(currentSelectedResponse.model)
            ) {
              return !currentSelectedResponse.model.includes(resp.model);
            }

            return true;
          });

          const greyConnectionKeys = [];
          nonSelectedResponses.forEach((response) => {
            const keys = drawGreyConnections(
              instance,
              elementRefs.current,
              response,
              subsequentResponses,
              userMessageIndex,
              aiResponsesIndex,
              themeValue,
              reorderedItemIds
            );
            greyConnectionKeys.push(...keys);
          });

          // Combine selected and grey connections
          if (currentSelectedResponse) {
            newConnectionsByInteractionState[interactionKey] = [
              ...newConnectionsByInteractionState[interactionKey],
              ...greyConnectionKeys,
            ];
          } else {
            newConnectionsByInteractionState[interactionKey] =
              greyConnectionKeys;
          }
        });

        connectionsByInteraction.current = newConnectionsByInteractionState;
      });
    },
    [messages, themeValue, reorderedItems]
  );

  const redrawPreviousInteractions = (instance, count) => {
    for (let i = 2; i < messages.length && i < count * 2 + 2; i += 2) {
      if (messages[i] && !Array.isArray(messages[i])) {
        redrawSingleInteraction(instance, i);
      }
    }
  };

  const redrawSingleInteraction = (instance, userMessageIndex) => {
    const userMessage = messages[userMessageIndex];

    if (!userMessage || Array.isArray(userMessage)) {
      return;
    }

    const { realKey: interactionKey } = processMessage(userMessage);

    const oldConnectionKeys =
      connectionsByInteraction.current[interactionKey] || [];

    oldConnectionKeys.forEach((connKey) => {
      const connection = currentConnections.current[connKey];
      if (connection) {
        try {
          instance.deleteConnection(connection);
        } catch (e) {}
        delete currentConnections.current[connKey];
      }
    });
    const aiResponsesIndex = userMessageIndex - 1;
    const subsequentResponses = getSubsequentResponses(aiResponsesIndex);
    const currentSelectedResponse =
      subsequentResponses.find((resp) => resp.selected === true) ||
      (subsequentResponses.length === 1 ? subsequentResponses[0] : null);

    if (
      currentSelectedResponse?.merged ||
      subsequentResponses.some((r) => r.merged)
    ) {
      Object.keys(elementRefs.current).forEach((key) => {
        const elementNode = elementRefs.current[key];
        if (
          elementNode &&
          (key.startsWith(`virtual-merge-point-`) ||
            key.match(new RegExp(`^merge-source-.+-msg-${userMessageIndex}$`)))
        ) {
          try {
            instance.remove(elementNode);
          } catch (err) {}
          elementNode.parentNode?.removeChild(elementNode);
          delete elementRefs.current[key];
        }
      });
    }

    const reorderedItemIds = reorderedItems[userMessageIndex] || null;

    if (currentSelectedResponse) {
      if (currentSelectedResponse.merged) {
        setTimeout(() => {
          const connectionKeys = drawMergeConnections(
            instance,
            elementRefs.current,
            currentSelectedResponse,
            subsequentResponses,
            userMessageIndex,
            aiResponsesIndex,
            true,
            reorderedItemIds
          );

          connectionsByInteraction.current[interactionKey] = connectionKeys;
          previousSelections.current[interactionKey] = {
            id: currentSelectedResponse._id,
            merged: currentSelectedResponse.merged,
            models: currentSelectedResponse.model,
          };
        }, 50);
      } else {
        updateConnectionsForInteraction(
          instance,
          elementRefs.current,
          interactionKey,
          userMessageIndex,
          aiResponsesIndex,
          currentSelectedResponse,
          subsequentResponses,
          connectionsByInteraction.current,
          themeValue,
          reorderedItemIds
        );
      }
    }
  };

  const createStreamingConnection = (instance) => {
    const latestUserMessage = messages?.[0];
    if (
      !latestUserMessage ||
      Array.isArray(latestUserMessage) ||
      latestUserMessage._id !== undefined
    ) {
      return;
    }

    const previousAiResponses = messages?.[1];
    if (!Array.isArray(previousAiResponses)) return;

    const selectedPreviousResponse =
      previousAiResponses.find((resp) => resp.selected === true) ||
      (previousAiResponses.length === 1 ? previousAiResponses[0] : null);

    if (!selectedPreviousResponse) return;

    let sourceElementId;
    if (selectedPreviousResponse.merged) {
      sourceElementId = `merge-source-${selectedPreviousResponse._id}-msg-1`;
    } else {
      sourceElementId = `card-${selectedPreviousResponse._id}`;
    }

    const targetElementId = 'user-top-bar-idx-undefined';
    const sourceNode = elementRefs.current[sourceElementId];
    const targetNode = elementRefs.current[targetElementId];

    if (sourceNode && targetNode) {
      try {
        const conn = instance.connect({
          source: sourceNode,
          target: targetNode,
          anchors: [
            [0.5, 1, 0, 1],
            [0.5, 0, 0, -1],
          ],
          connector: [
            'Flowchart',
            { cornerRadius: 40, stub: [10, 10], gap: 0 },
          ],
          paintStyle: {
            stroke: '#4F46E5',
            strokeWidth: 2,
            strokeDasharray: '5,5',
          },
        });
        streamingConnection.current = { connection: conn };
      } catch (error) {}
    }
  };

  const performFullCleanup = useCallback(
    (instance) => {
      try {
        instance.setSuspendDrawing(true);
        instance.deleteEveryConnection();
      } catch (e) {
      } finally {
        instance.setSuspendDrawing(false, true);
      }

      cleanupConnectionRefs();

      Object.keys(elementRefs.current).forEach((key) => {
        const elementNode = elementRefs.current[key];
        if (
          elementNode &&
          (key.startsWith('virtual-merge-point-') ||
            key.match(/^merge-source-.+-msg-\d+$/))
        ) {
          try {
            instance.remove(elementNode);
          } catch (err) {}
          elementNode.parentNode?.removeChild(elementNode);
          delete elementRefs.current[key];
        }
      });
    },
    [cleanupConnectionRefs]
  );

  const updateConnections = (currentInstance, refs) => {
    const newConnectionsByInteractionState = {
      ...connectionsByInteraction.current,
    };

    // Check if theme has changed
    const themeChanged = previousTheme.current !== themeValue;
    if (themeChanged) {
      previousTheme.current = themeValue;
    }

    currentInstance.batch(() => {
      messages.forEach((item, userMessageIndex) => {
        const { realKey: interactionKey, isUserMessage } = processMessage(item);
        if (!isUserMessage) return;

        const aiResponsesIndex = userMessageIndex - 1;
        const subsequentResponses = getSubsequentResponses(aiResponsesIndex);

        const currentSelectedResponse =
          subsequentResponses.find((resp) => resp.selected === true) ||
          (subsequentResponses.length === 1 ? subsequentResponses[0] : null);

        const prevSelectionDetails = previousSelections.current[interactionKey];
        const selectionChanged = hasSelectionChanged(
          prevSelectionDetails,
          currentSelectedResponse
        );

        // Extract reordered item IDs for this user message
        const reorderedItemIds = reorderedItems[userMessageIndex] || null;

        if (selectionChanged || themeChanged) {
          updateConnectionsForInteraction(
            currentInstance,
            refs,
            interactionKey,
            userMessageIndex,
            aiResponsesIndex,
            currentSelectedResponse,
            subsequentResponses,
            newConnectionsByInteractionState,
            themeValue,
            reorderedItemIds
          );
        } else {
          // Preserve existing connections but still draw grey connections for non-selected if not already done
          const existingKeys =
            connectionsByInteraction.current[interactionKey] || [];
          const hasGreyConnections = existingKeys.some((key) =>
            key.includes('grey-')
          );

          if (!hasGreyConnections) {
            const nonSelectedResponses = subsequentResponses.filter((resp) => {
              // Skip merged responses
              if (resp.merged) return false;

              // Skip the currently selected response
              if (
                currentSelectedResponse &&
                resp._id === currentSelectedResponse._id
              )
                return false;

              // Skip responses that are sources for a selected merge (they already get purple arrows)
              if (
                currentSelectedResponse?.merged &&
                Array.isArray(currentSelectedResponse.model)
              ) {
                return !currentSelectedResponse.model.includes(resp.model);
              }

              return true;
            });

            const greyConnectionKeys = [];
            nonSelectedResponses.forEach((response) => {
              const keys = drawGreyConnections(
                currentInstance,
                refs,
                response,
                subsequentResponses,
                userMessageIndex,
                aiResponsesIndex,
                themeValue,
                reorderedItemIds
              );
              greyConnectionKeys.push(...keys);
            });

            newConnectionsByInteractionState[interactionKey] = [
              ...existingKeys,
              ...greyConnectionKeys,
            ];
          } else {
            // Preserve existing connections
            newConnectionsByInteractionState[interactionKey] = existingKeys;
          }
        }
      });

      connectionsByInteraction.current = newConnectionsByInteractionState;
    });
  };

  const getSubsequentResponses = (aiResponsesIndex) => {
    if (aiResponsesIndex < 0) return [];

    if (Array.isArray(messages[aiResponsesIndex])) {
      return messages[aiResponsesIndex];
    } else if (messages[aiResponsesIndex]?.data?.role === 'assistant') {
      return [messages[aiResponsesIndex]];
    }
    return [];
  };

  const hasSelectionChanged = (
    prevSelectionDetails,
    currentSelectedResponse
  ) => {
    if (!prevSelectionDetails && currentSelectedResponse) {
      return true;
    }
    if (prevSelectionDetails && !currentSelectedResponse) {
      return true;
    }
    if (prevSelectionDetails && currentSelectedResponse) {
      const idChanged = prevSelectionDetails.id !== currentSelectedResponse._id;
      const mergedChanged =
        prevSelectionDetails.merged !== currentSelectedResponse.merged;

      let modelsChanged = false;
      if (
        currentSelectedResponse.merged &&
        Array.isArray(currentSelectedResponse.model) &&
        Array.isArray(prevSelectionDetails.models)
      ) {
        modelsChanged =
          JSON.stringify(prevSelectionDetails.models.sort()) !==
          JSON.stringify(currentSelectedResponse.model.sort());
      }

      return idChanged || mergedChanged || modelsChanged;
    }
    return false;
  };

  const updateConnectionsForInteraction = (
    currentInstance,
    refs,
    interactionKey,
    userMessageIndex,
    aiResponsesIndex,
    currentSelectedResponse,
    subsequentResponses,
    newConnectionsByInteractionState,
    themeValue,
    reorderedItemIds = null
  ) => {
    const oldConnectionKeys =
      connectionsByInteraction.current[interactionKey] || [];

    oldConnectionKeys.forEach((connKey) => {
      const connection = currentConnections.current[connKey];
      if (connection) {
        try {
          currentInstance.deleteConnection(connection);
        } catch (e) {}
        delete currentConnections.current[connKey];
      }
    });
    const prevSelectionDetails = previousSelections.current[interactionKey];
    if (prevSelectionDetails?.merged) {
      cleanupOldMergeElements(
        currentInstance,
        refs,
        prevSelectionDetails.id,
        userMessageIndex
      );
    }

    const currentInteractionNewConnectionKeys = [];
    if (currentSelectedResponse) {
      const connectionKeys = currentSelectedResponse.merged
        ? drawMergeConnections(
            currentInstance,
            refs,
            currentSelectedResponse,
            subsequentResponses,
            userMessageIndex,
            aiResponsesIndex,
            true,
            reorderedItemIds
          )
        : drawNormalConnections(
            currentInstance,
            refs,
            currentSelectedResponse,
            subsequentResponses,
            userMessageIndex,
            aiResponsesIndex,
            reorderedItemIds
          );

      currentInteractionNewConnectionKeys.push(...connectionKeys);
    }

    const nonSelectedResponses = subsequentResponses.filter((resp) => {
      if (resp.merged) return false;
      if (currentSelectedResponse && resp._id === currentSelectedResponse._id)
        return false;
      if (
        currentSelectedResponse?.merged &&
        Array.isArray(currentSelectedResponse.model)
      ) {
        return !currentSelectedResponse.model.includes(resp.model);
      }
      return true;
    });

    nonSelectedResponses.forEach((response) => {
      const keys = drawGreyConnections(
        currentInstance,
        refs,
        response,
        subsequentResponses,
        userMessageIndex,
        aiResponsesIndex,
        themeValue,
        reorderedItemIds
      );
      currentInteractionNewConnectionKeys.push(...keys);
    });

    newConnectionsByInteractionState[interactionKey] =
      currentInteractionNewConnectionKeys;

    const newSelectionData = currentSelectedResponse
      ? {
          id: currentSelectedResponse._id,
          merged: currentSelectedResponse.merged,
          models: currentSelectedResponse.model,
        }
      : null;

    previousSelections.current[interactionKey] = newSelectionData;
  };

  const cleanupOldMergeElements = (
    currentInstance,
    refs,
    mergeId,
    userMessageIndex
  ) => {
    const elementsToCleanup = [
      `virtual-merge-point-${mergeId}`,
      `merge-source-${mergeId}-msg-${userMessageIndex}`,
    ];

    elementsToCleanup.forEach((elementId) => {
      if (refs[elementId]) {
        const elementNode = refs[elementId];
        try {
          currentInstance.remove(elementNode);
        } catch (e) {
          // Silent cleanup
        }
        if (elementNode.parentNode) {
          elementNode.parentNode.removeChild(elementNode);
        }
        delete refs[elementId];
      }
    });
  };

  const drawNormalConnections = (
    currentInstance,
    refs,
    currentSelectedResponse,
    subsequentResponses,
    userMessageIndex,
    aiResponsesIndex,
    reorderedItemIds = null
  ) => {
    const connectionKeys = [];
    const connectionColor = '#4F46E5';
    const nonMergedResponses = subsequentResponses.filter(
      (resp) => !resp.merged
    );
    const totalNonMergedCount = nonMergedResponses.length;

    const selectedIndexInSubsequent =
      subsequentResponses.length === 1
        ? 0
        : subsequentResponses.findIndex(
            (r) => r._id === currentSelectedResponse._id
          );

    if (selectedIndexInSubsequent !== -1) {
      const selectedIndexInNonMerged = nonMergedResponses.findIndex(
        (r) => r._id === currentSelectedResponse._id
      );

      const visualPositionMap = {};
      if (reorderedItemIds) {
        reorderedItemIds.forEach((id, visualIndex) => {
          visualPositionMap[id] = visualIndex;
        });
      }

      let visualPosition = selectedIndexInNonMerged;
      if (reorderedItemIds && totalNonMergedCount > 1) {
        const reorderedIndex = visualPositionMap[currentSelectedResponse._id];
        if (reorderedIndex !== undefined) {
          visualPosition = reorderedIndex;
        }
      }
      let connectionConnector;
      if (totalNonMergedCount === 4 && selectedIndexInNonMerged !== -1) {
        if (visualPosition === 0 || visualPosition === 3) {
          connectionConnector = [
            'Flowchart',
            { cornerRadius: 10, stub: [10, 10], gap: 0, midpoint: 0.35 },
          ];
        } else {
          connectionConnector = [
            'Flowchart',
            { cornerRadius: 10, stub: [10, 10], gap: 0, midpoint: 0.65 },
          ];
        }
      } else if (totalNonMergedCount === 3 && visualPosition === 1) {
        connectionConnector = ['Straight'];
      } else {
        connectionConnector = [
          'Flowchart',
          { cornerRadius: 40, stub: [10, 10], gap: 0 },
        ];
      }

      const sourceDotId = `dot-${userMessageIndex}-${selectedIndexInSubsequent}`;
      const targetCardId = `card-${currentSelectedResponse._id}`;

      const dotToCardKey = connectElements(
        currentInstance,
        refs[sourceDotId],
        refs[targetCardId],
        `${sourceDotId}-${targetCardId}`,
        connectionColor,
        connectionConnector
      );
      if (dotToCardKey) connectionKeys.push(dotToCardKey);

      const hasSmartMergeNotSelected = () => {
        const mergeItems = subsequentResponses.filter((resp) => resp.merged);
        const selectedIsMerge = mergeItems.some((merge) => merge.selected);
        return mergeItems.length > 0 && !selectedIsMerge;
      };

      const cardToNextUserConnector = hasSmartMergeNotSelected()
        ? [
            'Flowchart',
            { cornerRadius: 40, stub: [10, 10], gap: 0, midpoint: 0.98 },
          ]
        : connectionConnector;

      const cardToNextUserKey = connectCardToNextUser(
        currentInstance,
        refs,
        messages,
        aiResponsesIndex,
        targetCardId,
        connectionColor,
        cardToNextUserConnector,
        false,
        userMessageIndex
      );
      if (cardToNextUserKey) connectionKeys.push(cardToNextUserKey);
    }

    return connectionKeys;
  };

  const drawGreyConnections = (
    currentInstance,
    refs,
    response,
    subsequentResponses,
    userMessageIndex,
    aiResponsesIndex,
    themeValue,
    reorderedItemIds = null
  ) => {
    const connectionKeys = [];
    const greyColor = themeValue === 'light' ? '#9CA3AF' : '#4B5563';
    const nonMergedResponses = subsequentResponses.filter(
      (resp) => !resp.merged
    );
    const totalNonMergedCount = nonMergedResponses.length;

    const responseIndexInSubsequent = subsequentResponses.findIndex(
      (r) => r._id === response._id
    );

    if (responseIndexInSubsequent !== -1) {
      const responseIndexInNonMerged = nonMergedResponses.findIndex(
        (r) => r._id === response._id
      );

      const visualPositionMap = {};
      if (reorderedItemIds) {
        reorderedItemIds.forEach((id, visualIndex) => {
          visualPositionMap[id] = visualIndex;
        });
      }

      let visualPosition = responseIndexInNonMerged;
      if (reorderedItemIds && totalNonMergedCount > 1) {
        const reorderedIndex = visualPositionMap[response._id];
        if (reorderedIndex !== undefined) {
          visualPosition = reorderedIndex;
        }
      }

      let connectionConnector;
      if (totalNonMergedCount === 4 && responseIndexInNonMerged !== -1) {
        connectionConnector =
          visualPosition === 0 || visualPosition === 3
            ? [
                'Flowchart',
                { cornerRadius: 10, stub: [10, 10], gap: 0, midpoint: 0.35 },
              ]
            : [
                'Flowchart',
                { cornerRadius: 10, stub: [10, 10], gap: 0, midpoint: 0.65 },
              ];
      } else if (totalNonMergedCount === 3 && visualPosition === 1) {
        connectionConnector = ['Straight'];
      } else {
        connectionConnector = [
          'Flowchart',
          { cornerRadius: 40, stub: [10, 10], gap: 0 },
        ];
      }

      const sourceDotId = `dot-${userMessageIndex}-${responseIndexInSubsequent}`;
      const targetCardId = `card-${response._id}`;

      const dotToCardKey = connectElements(
        currentInstance,
        refs[sourceDotId],
        refs[targetCardId],
        `grey-${sourceDotId}-${targetCardId}`,
        greyColor,
        connectionConnector
      );
      if (dotToCardKey) connectionKeys.push(dotToCardKey);
    }

    return connectionKeys;
  };

  const drawMergeConnections = (
    currentInstance,
    refs,
    currentSelectedResponse,
    subsequentResponses,
    userMessageIndex,
    aiResponsesIndex,
    directMode = true,
    reorderedItemIds = null
  ) => {
    const connectionKeys = [];
    const connectionColor = '#4F46E5';
    const connectionConnector = [
      'Flowchart',
      { cornerRadius: 45, stub: [0, 10], gap: 0, midpoint: 0.5 },
    ];

    if (!Array.isArray(currentSelectedResponse.model)) return connectionKeys;

    const sourceModels = currentSelectedResponse.model;
    const targetMergeCardId = `merge-card-${currentSelectedResponse._id}`;
    const targetMergeCardNode = refs[targetMergeCardId];

    const nonMergedResponses = subsequentResponses.filter(
      (resp) => !resp.merged
    );

    const isCase1 = detectCase1(sourceModels, nonMergedResponses);
    const isCase2 = detectCase2(sourceModels, nonMergedResponses);

    if (isCase1) {
      return drawCase1MergeConnections(
        currentInstance,
        refs,
        currentSelectedResponse,
        subsequentResponses,
        userMessageIndex,
        aiResponsesIndex,
        sourceModels,
        targetMergeCardId,
        targetMergeCardNode,
        connectionColor,
        connectionConnector,
        reorderedItemIds
      );
    }

    if (isCase2) {
      return drawCase2MergeConnections(
        currentInstance,
        refs,
        currentSelectedResponse,
        subsequentResponses,
        userMessageIndex,
        aiResponsesIndex,
        sourceModels,
        targetMergeCardId,
        targetMergeCardNode,
        connectionColor,
        connectionConnector,
        reorderedItemIds
      );
    }

    if (directMode) {
      const visualPositionMap = {};
      if (reorderedItemIds) {
        reorderedItemIds.forEach((id, visualIndex) => {
          visualPositionMap[id] = visualIndex;
        });
      }
      subsequentResponses.forEach((resp, respIndex) => {
        if (!resp.merged && sourceModels.includes(resp.model)) {
          const sourceDotId = `dot-${userMessageIndex}-${respIndex}`;
          const sourceCardId = `card-${resp._id}`;

          let visualPosition = nonMergedResponses.findIndex(
            (r) => r._id === resp._id
          );
          if (reorderedItemIds && nonMergedResponses.length > 1) {
            const reorderedIndex = visualPositionMap[resp._id];
            if (reorderedIndex !== undefined) {
              visualPosition = reorderedIndex;
            }
          }

          let dotToCardConnector;
          if (nonMergedResponses.length === 4) {
            dotToCardConnector =
              visualPosition === 0 || visualPosition === 3
                ? [
                    'Flowchart',
                    {
                      cornerRadius: 10,
                      stub: [10, 10],
                      gap: 0,
                      midpoint: 0.35,
                    },
                  ]
                : [
                    'Flowchart',
                    {
                      cornerRadius: 10,
                      stub: [10, 10],
                      gap: 0,
                      midpoint: 0.65,
                    },
                  ];
          } else {
            dotToCardConnector = connectionConnector;
          }
          const dotToCardKey = connectElements(
            currentInstance,
            refs[sourceDotId],
            refs[sourceCardId],
            `${sourceDotId}-${sourceCardId}`,
            connectionColor,
            dotToCardConnector
          );
          if (dotToCardKey) connectionKeys.push(dotToCardKey);

          if (refs[sourceCardId] && targetMergeCardNode) {
            const cardToMergeKey = connectElements(
              currentInstance,
              refs[sourceCardId],
              targetMergeCardNode,
              `${sourceCardId}-to-${targetMergeCardId}-direct-msg-${userMessageIndex}`,
              connectionColor,
              connectionConnector
            );
            if (cardToMergeKey) connectionKeys.push(cardToMergeKey);
          }
        }
      });

      const mergeSourceId = `merge-source-${currentSelectedResponse._id}-msg-${userMessageIndex}`;
      let mergeSourcePoint = refs[mergeSourceId];

      if (!mergeSourcePoint && targetMergeCardNode) {
        mergeSourcePoint = createMergeSourcePoint(
          mergeSourceId,
          targetMergeCardNode
        );
      }
      const cardToNextUserKey = connectCardToNextUser(
        currentInstance,
        refs,
        messages,
        aiResponsesIndex,
        targetMergeCardId,
        connectionColor,
        connectionConnector,
        true,
        userMessageIndex
      );
      if (cardToNextUserKey) connectionKeys.push(cardToNextUserKey);

      return connectionKeys;
    }

    const virtualMergePointId = `virtual-merge-point-${currentSelectedResponse._id}`;
    let virtualPoint = refs[virtualMergePointId];

    if (!virtualPoint && targetMergeCardNode) {
      virtualPoint = createVirtualMergePoint(
        virtualMergePointId,
        targetMergeCardNode,
        subsequentResponses,
        sourceModels,
        refs,
        userMessageIndex,
        currentSelectedResponse._id
      );
    }

    const mergeSourceId = `merge-source-${currentSelectedResponse._id}-msg-${userMessageIndex}`;
    let mergeSourcePoint = refs[mergeSourceId];

    if (!mergeSourcePoint && targetMergeCardNode) {
      mergeSourcePoint = createMergeSourcePoint(
        mergeSourceId,
        targetMergeCardNode
      );
    }

    const visualPositionMap = {};
    if (reorderedItemIds) {
      reorderedItemIds.forEach((id, visualIndex) => {
        visualPositionMap[id] = visualIndex;
      });
    }

    subsequentResponses.forEach((resp, respIndex) => {
      if (!resp.merged && sourceModels.includes(resp.model)) {
        const sourceDotId = `dot-${userMessageIndex}-${respIndex}`;
        const sourceCardId = `card-${resp._id}`;

        let visualPosition = nonMergedResponses.findIndex(
          (r) => r._id === resp._id
        );
        if (reorderedItemIds && nonMergedResponses.length > 1) {
          const reorderedIndex = visualPositionMap[resp._id];
          if (reorderedIndex !== undefined) {
            visualPosition = reorderedIndex;
          }
        }

        let dotToCardConnector;
        if (nonMergedResponses.length === 4) {
          dotToCardConnector =
            visualPosition === 0 || visualPosition === 3
              ? [
                  'Flowchart',
                  { cornerRadius: 10, stub: [10, 10], gap: 0, midpoint: 0.35 },
                ]
              : [
                  'Flowchart',
                  { cornerRadius: 10, stub: [10, 10], gap: 0, midpoint: 0.65 },
                ];
        } else {
          dotToCardConnector = connectionConnector;
        }

        const dotToCardKey = connectElements(
          currentInstance,
          refs[sourceDotId],
          refs[sourceCardId],
          `${sourceDotId}-${sourceCardId}`,
          connectionColor,
          dotToCardConnector
        );
        if (dotToCardKey) connectionKeys.push(dotToCardKey);

        if (refs[sourceCardId] && virtualPoint) {
          const cardToVirtualKey = connectElements(
            currentInstance,
            refs[sourceCardId],
            virtualPoint,
            `${sourceCardId}-to-${virtualMergePointId}-msg-${userMessageIndex}`,
            connectionColor,
            connectionConnector
          );
          if (cardToVirtualKey) connectionKeys.push(cardToVirtualKey);
        }
      }
    });

    if (virtualPoint && targetMergeCardNode) {
      const virtualToMergeKey = connectElements(
        currentInstance,
        virtualPoint,
        targetMergeCardNode,
        `${virtualMergePointId}-to-${targetMergeCardId}-msg-${userMessageIndex}`,
        '#f2a474',
        connectionConnector
      );
      if (virtualToMergeKey) connectionKeys.push(virtualToMergeKey);
    }

    const cardToNextUserKey = connectCardToNextUser(
      currentInstance,
      refs,
      messages,
      aiResponsesIndex,
      targetMergeCardId,
      connectionColor,
      connectionConnector,
      true,
      userMessageIndex
    );
    if (cardToNextUserKey) connectionKeys.push(cardToNextUserKey);

    return connectionKeys;
  };

  const detectCase1 = (sourceModels, nonMergedResponses) => {
    if (sourceModels.length !== 2 || nonMergedResponses.length < 3)
      return false;

    const selectedIndices = [];
    nonMergedResponses.forEach((resp, index) => {
      if (sourceModels.includes(resp.model)) {
        selectedIndices.push(index);
      }
    });

    return (
      selectedIndices.length === 2 &&
      Math.abs(selectedIndices[0] - selectedIndices[1]) === 1
    );
  };

  const detectCase2 = (sourceModels, nonMergedResponses) => {
    return (
      sourceModels.length === nonMergedResponses.length &&
      nonMergedResponses.length === 3
    );
  };

  const drawCase1MergeConnections = (
    currentInstance,
    refs,
    currentSelectedResponse,
    subsequentResponses,
    userMessageIndex,
    aiResponsesIndex,
    sourceModels,
    targetMergeCardId,
    targetMergeCardNode,
    connectionColor,
    connectionConnector,
    reorderedItemIds = null
  ) => {
    const connectionKeys = [];

    const visualPositionMap = {};
    if (reorderedItemIds) {
      reorderedItemIds.forEach((id, visualIndex) => {
        visualPositionMap[id] = visualIndex;
      });
    }

    const virtualMergePointId = `virtual-merge-point-${currentSelectedResponse._id}`;
    let virtualPoint = refs[virtualMergePointId];

    if (!virtualPoint && targetMergeCardNode) {
      virtualPoint = createInvisibleVirtualMergePoint(
        virtualMergePointId,
        targetMergeCardNode,
        subsequentResponses,
        sourceModels,
        refs,
        userMessageIndex,
        currentSelectedResponse._id
      );
    }

    const mergeSourceId = `merge-source-${currentSelectedResponse._id}-msg-${userMessageIndex}`;
    let mergeSourcePoint = refs[mergeSourceId];

    if (!mergeSourcePoint && targetMergeCardNode) {
      mergeSourcePoint = createMergeSourcePoint(
        mergeSourceId,
        targetMergeCardNode
      );
    }

    const nonMergedResponses = subsequentResponses.filter(
      (resp) => !resp.merged
    );

    subsequentResponses.forEach((resp, respIndex) => {
      if (!resp.merged && sourceModels.includes(resp.model)) {
        const sourceDotId = `dot-${userMessageIndex}-${respIndex}`;
        const sourceCardId = `card-${resp._id}`;

        let visualPosition = nonMergedResponses.findIndex(
          (r) => r._id === resp._id
        );
        if (reorderedItemIds) {
          const reorderedIndex = visualPositionMap[resp._id];
          if (reorderedIndex !== undefined) {
            visualPosition = reorderedIndex;
          }
        }

        let dotToCardConnector;
        if (nonMergedResponses.length === 4) {
          dotToCardConnector =
            visualPosition === 0 || visualPosition === 3
              ? [
                  'Flowchart',
                  { cornerRadius: 10, stub: [10, 10], gap: 0, midpoint: 0.35 },
                ]
              : [
                  'Flowchart',
                  { cornerRadius: 10, stub: [10, 10], gap: 0, midpoint: 0.65 },
                ];
        } else if (nonMergedResponses.length === 3 && visualPosition === 1) {
          dotToCardConnector = ['Straight'];
        } else {
          dotToCardConnector = connectionConnector;
        }

        const dotToCardKey = connectElements(
          currentInstance,
          refs[sourceDotId],
          refs[sourceCardId],
          `${sourceDotId}-${sourceCardId}`,
          connectionColor,
          dotToCardConnector
        );
        if (dotToCardKey) connectionKeys.push(dotToCardKey);

        if (refs[sourceCardId] && virtualPoint) {
          const cardToVirtualKey = connectElements(
            currentInstance,
            refs[sourceCardId],
            virtualPoint,
            `${sourceCardId}-to-${virtualMergePointId}-msg-${userMessageIndex}`,
            connectionColor,
            connectionConnector
          );
          if (cardToVirtualKey) connectionKeys.push(cardToVirtualKey);
        }
      }
    });

    if (virtualPoint && targetMergeCardNode) {
      const virtualToMergeKey = connectElements(
        currentInstance,
        virtualPoint,
        targetMergeCardNode,
        `${virtualMergePointId}-to-${targetMergeCardId}-msg-${userMessageIndex}`,
        connectionColor,
        connectionConnector
      );
      if (virtualToMergeKey) connectionKeys.push(virtualToMergeKey);
    }

    const cardToNextUserKey = connectCardToNextUser(
      currentInstance,
      refs,
      messages,
      aiResponsesIndex,
      targetMergeCardId,
      connectionColor,
      connectionConnector,
      true,
      userMessageIndex
    );
    if (cardToNextUserKey) connectionKeys.push(cardToNextUserKey);

    return connectionKeys;
  };

  const drawCase2MergeConnections = (
    currentInstance,
    refs,
    currentSelectedResponse,
    subsequentResponses,
    userMessageIndex,
    aiResponsesIndex,
    sourceModels,
    targetMergeCardId,
    targetMergeCardNode,
    connectionColor,
    connectionConnector,
    reorderedItemIds = null
  ) => {
    const connectionKeys = [];
    const straightConnector = ['Straight'];
    const nonMergedResponses = subsequentResponses.filter(
      (resp) => !resp.merged
    );

    const visualPositionMap = {};
    if (reorderedItemIds) {
      reorderedItemIds.forEach((id, visualIndex) => {
        visualPositionMap[id] = visualIndex;
      });
    }

    subsequentResponses.forEach((resp, respIndex) => {
      if (!resp.merged && sourceModels.includes(resp.model)) {
        const sourceDotId = `dot-${userMessageIndex}-${respIndex}`;
        const sourceCardId = `card-${resp._id}`;

        const dotToCardKey = connectElements(
          currentInstance,
          refs[sourceDotId],
          refs[sourceCardId],
          `${sourceDotId}-${sourceCardId}`,
          connectionColor,
          connectionConnector
        );
        if (dotToCardKey) connectionKeys.push(dotToCardKey);

        let isMiddleResponse =
          respIndex === Math.floor(nonMergedResponses.length / 2);

        if (reorderedItemIds && nonMergedResponses.length > 1) {
          const visualPosition = visualPositionMap[resp._id];
          if (visualPosition !== undefined) {
            isMiddleResponse =
              visualPosition === Math.floor(nonMergedResponses.length / 2);
          }
        }

        if (refs[sourceCardId] && targetMergeCardNode) {
          const cardToMergeKey = connectElements(
            currentInstance,
            refs[sourceCardId],
            targetMergeCardNode,
            `${sourceCardId}-to-${targetMergeCardId}-direct-msg-${userMessageIndex}`,
            connectionColor,
            isMiddleResponse ? straightConnector : connectionConnector
          );
          if (cardToMergeKey) connectionKeys.push(cardToMergeKey);
        }
      }
    });

    const mergeSourceId = `merge-source-${currentSelectedResponse._id}-msg-${userMessageIndex}`;
    let mergeSourcePoint = refs[mergeSourceId];

    if (!mergeSourcePoint && targetMergeCardNode) {
      mergeSourcePoint = createMergeSourcePoint(
        mergeSourceId,
        targetMergeCardNode
      );
    }

    const cardToNextUserKey = connectCardToNextUser(
      currentInstance,
      refs,
      messages,
      aiResponsesIndex,
      targetMergeCardId,
      connectionColor,
      connectionConnector,
      true,
      userMessageIndex
    );
    if (cardToNextUserKey) connectionKeys.push(cardToNextUserKey);

    return connectionKeys;
  };

  const createVirtualMergePoint = (
    virtualMergePointId,
    targetMergeCardNode,
    subsequentResponses,
    sourceModels,
    refs,
    userMessageIndex,
    responseId
  ) => {
    const virtualPoint = document.createElement('div');
    virtualPoint.id = virtualMergePointId;

    const { top, left } = calculateVirtualMergePointPosition(
      targetMergeCardNode,
      subsequentResponses,
      sourceModels,
      refs,
      plumbContainerRef.current
    );

    Object.assign(virtualPoint.style, {
      position: 'absolute',
      width: '12px',
      height: '12px',
      zIndex: '10',
      borderRadius: '50%',
      backgroundColor: '#f2a474',
      border: '1px solid rgba(242, 164, 116, 0.8)',
      pointerEvents: 'none',
      top: `${top}px`,
      left: `${left}px`,
    });

    virtualPoint.setAttribute('data-purpose', 'merge-connection-point');
    virtualPoint.setAttribute('data-message-index', userMessageIndex);
    virtualPoint.setAttribute('data-response-id', responseId);

    plumbContainerRef.current.appendChild(virtualPoint);
    refs[virtualMergePointId] = virtualPoint;

    return virtualPoint;
  };

  const createInvisibleVirtualMergePoint = (
    virtualMergePointId,
    targetMergeCardNode,
    subsequentResponses,
    sourceModels,
    refs,
    userMessageIndex,
    responseId
  ) => {
    const virtualPoint = document.createElement('div');
    virtualPoint.id = virtualMergePointId;

    const { top, left } = calculateVirtualMergePointPosition(
      targetMergeCardNode,
      subsequentResponses,
      sourceModels,
      refs,
      plumbContainerRef.current
    );

    Object.assign(virtualPoint.style, {
      position: 'absolute',
      width: '6px',
      height: '6px',
      zIndex: '10',
      borderRadius: '50%',
      backgroundColor: '#4F46E5', // Purple color to match connections
      pointerEvents: 'none',
      top: `${top}px`,
      left: `${left}px`,
    });

    virtualPoint.setAttribute('data-purpose', 'merge-connection-point');
    virtualPoint.setAttribute('data-message-index', userMessageIndex);
    virtualPoint.setAttribute('data-response-id', responseId);

    plumbContainerRef.current.appendChild(virtualPoint);
    refs[virtualMergePointId] = virtualPoint;

    return virtualPoint;
  };

  const createMergeSourcePoint = (mergeSourceId, targetMergeCardNode) => {
    const mergeSourcePoint = document.createElement('div');
    mergeSourcePoint.id = mergeSourceId;

    const { top, left } = calculateMergeSourcePointPosition(
      targetMergeCardNode,
      plumbContainerRef.current
    );

    Object.assign(mergeSourcePoint.style, {
      position: 'absolute',
      width: '4px',
      height: '4px',
      backgroundColor: 'transparent',
      pointerEvents: 'none',
      zIndex: '5',
      top: `${top}px`,
      left: `${left}px`,
    });

    plumbContainerRef.current.appendChild(mergeSourcePoint);
    elementRefs.current[mergeSourceId] = mergeSourcePoint;

    return mergeSourcePoint;
  };

  const connectElements = (
    instance,
    sourceNode,
    targetNode,
    connectionKey,
    color,
    connector,
    customAnchors = null
  ) => {
    if (!sourceNode || !targetNode) return null;

    try {
      const conn = instance.connect({
        source: sourceNode,
        target: targetNode,
        anchors: customAnchors || [
          [0.5, 1, 0, 1],
          [0.5, 0, 0, -1],
        ],
        connector: connector,
        paintStyle: { stroke: color, strokeWidth: 2 },
      });
      currentConnections.current[connectionKey] = conn;
      return connectionKey;
    } catch (error) {
      return null;
    }
  };

  const connectCardToNextUser = (
    instance,
    refs,
    messages,
    currentAiIndex,
    sourceCardId,
    color,
    connector,
    isMergeSource = false,
    messageIndex = null
  ) => {
    const nextUserMessageIndex = currentAiIndex - 1;
    if (nextUserMessageIndex < 0) return null;

    const nextMessageItem = messages[nextUserMessageIndex];
    if (!nextMessageItem || Array.isArray(nextMessageItem)) return null;

    const sourceNodeId = isMergeSource
      ? `merge-source-${sourceCardId.split('-')[2]}-msg-${messageIndex}`
      : sourceCardId;

    const targetUserBarId = `user-top-bar-idx-${nextUserMessageIndex}`;
    const sourceNode = refs[sourceNodeId];
    const targetUserBarNode = refs[targetUserBarId];

    if (sourceNode && targetUserBarNode) {
      const connectionKey = `${sourceNodeId}-to-${targetUserBarId}`;
      return connectElements(
        instance,
        sourceNode,
        targetUserBarNode,
        connectionKey,
        color,
        connector
      );
    }

    return null;
  };

  const calculateVirtualMergePointPosition = (
    targetMergeCardNode,
    subsequentResponses,
    sourceModels,
    elementRefs,
    plumbContainer
  ) => {
    if (!targetMergeCardNode || !plumbContainer) return { top: 0, left: 0 };

    const targetRect = targetMergeCardNode.getBoundingClientRect();
    const containerRect = plumbContainer.getBoundingClientRect();

    const sourceCardPositions = subsequentResponses
      .filter((resp) => !resp.merged && sourceModels.includes(resp.model))
      .map((resp) => {
        const sourceCardNode = elementRefs[`card-${resp._id}`];
        if (sourceCardNode) {
          const sourceRect = sourceCardNode.getBoundingClientRect();
          return {
            left: sourceRect.left + sourceRect.width / 2,
            bottom: sourceRect.bottom,
          };
        }
        return null;
      })
      .filter(Boolean);

    if (sourceCardPositions.length > 0) {
      const avgLeft =
        sourceCardPositions.reduce((sum, pos) => sum + pos.left, 0) /
        sourceCardPositions.length;
      const maxBottom = Math.max(
        ...sourceCardPositions.map((pos) => pos.bottom)
      );
      const mergeCardTop = targetRect.top - containerRect.top;
      const virtualPointTop =
        mergeCardTop - (mergeCardTop - (maxBottom - containerRect.top)) * 0.55;

      return {
        top: virtualPointTop,
        left: avgLeft - containerRect.left - 10,
      };
    }

    return {
      top: targetRect.top - containerRect.top - 100,
      left: targetRect.left + targetRect.width / 2 - containerRect.left - 10,
    };
  };

  const calculateMergeSourcePointPosition = (
    targetMergeCardNode,
    plumbContainer
  ) => {
    if (!targetMergeCardNode || !plumbContainer) return { top: 0, left: 0 };

    const targetRect = targetMergeCardNode.getBoundingClientRect();
    const containerRect = plumbContainer.getBoundingClientRect();

    return {
      top: targetRect.bottom - containerRect.top - 2,
      left: targetRect.left + targetRect.width / 2 - containerRect.left - 2,
    };
  };

  const debounce = useCallback((func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }, []);

  const handleDotClick = useCallback(
    (messageIndex, responseIndex) => {
      const aiResponsesIndex = messageIndex - 1;
      if (aiResponsesIndex < 0 || !Array.isArray(messages[aiResponsesIndex])) {
        return;
      }

      const targetResponse = messages[aiResponsesIndex]?.[responseIndex];
      if (!targetResponse) {
        return;
      }

      const isModelProviderError = useChatStore.getState().isModelProviderError;
      if (isModelProviderError(targetResponse)) {
        return;
      }
      const mergeActive = useSmartMergeStore.getState().active;
      if (mergeActive) {
        setSelectionChangeWarning({
          open: true,
          pendingSelection: { messageIndex, responseIndex },
        });
        return;
      }

      if (!isMostRecentUserMessage(messageIndex)) {
        setConversationPathWarning({
          open: true,
          pendingSelection: { messageIndex, responseIndex },
        });
        return;
      }
      if (reorderedItems[messageIndex]) {
        setReorderedItems((prev) => {
          const newReorderedItems = { ...prev };
          delete newReorderedItems[messageIndex];
          return newReorderedItems;
        });

        setDotReorderSignal({
          userMessageIndex: messageIndex,
          newOrder: null,
          timestamp: Date.now(),
        });
      }

      changeOutput(targetResponse);
    },
    [messages, changeOutput, reorderedItems, isMostRecentUserMessage]
  );

  const handleCardReorder = useCallback(
    (aiResponseArrayIndex, newOrder = null) => {
      const instance = jsPlumbInstance.current;
      if (!instance || renderType !== COLUMNS || !messages?.length) return;

      if (newOrder) {
        const aiResponses = messages[aiResponseArrayIndex];
        const originalNonMergedResponses = Array.isArray(aiResponses)
          ? aiResponses.filter((resp) => !resp.merged)
          : [];

        let newDotOrder = null;
        if (originalNonMergedResponses.length > 0) {
          const cardIdToOriginalPosition = {};
          originalNonMergedResponses.forEach((resp, originalIndex) => {
            cardIdToOriginalPosition[resp._id] = originalIndex;
          });

          newDotOrder = newOrder
            .map((cardId) => cardIdToOriginalPosition[cardId])
            .filter((pos) => pos !== undefined);
        }

        const correspondingUserMessageIndex = aiResponseArrayIndex + 1;
        setReorderedItems((prev) => ({
          ...prev,
          [correspondingUserMessageIndex]: newOrder,
        }));

        setDotReorderSignal({
          userMessageIndex: correspondingUserMessageIndex,
          newOrder,
          newDotOrder,
          timestamp: Date.now(),
        });

        setTimeout(() => setDotReorderSignal(null), 100);
      }

      setTimeout(
        () =>
          redrawAIResponseConnections(
            instance,
            aiResponseArrayIndex,
            messages,
            newOrder
          ),
        50
      );
    },
    [renderType, messages]
  );

  const redrawAIResponseConnections = (
    instance,
    aiResponseArrayIndex,
    currentMessages,
    reorderedItemIds = null
  ) => {
    const correspondingUserMessageIndex = aiResponseArrayIndex + 1;

    if (correspondingUserMessageIndex >= currentMessages.length) {
      return;
    }

    const correspondingUserMessage =
      currentMessages[correspondingUserMessageIndex];

    if (!correspondingUserMessage || Array.isArray(correspondingUserMessage)) {
      return;
    }

    const aiResponses = currentMessages[aiResponseArrayIndex];

    if (!Array.isArray(aiResponses)) {
      return;
    }

    const currentSelectedResponse =
      aiResponses.find((resp) => resp.selected === true) ||
      (aiResponses.length === 1 ? aiResponses[0] : null);

    if (!currentSelectedResponse) {
      return;
    }

    const { realKey: interactionKey } = processMessage(
      correspondingUserMessage
    );

    const oldConnectionKeys =
      connectionsByInteraction.current[interactionKey] || [];

    oldConnectionKeys.forEach((connKey) => {
      if (currentConnections.current[connKey]) {
        try {
          instance.deleteConnection(currentConnections.current[connKey]);
        } catch (e) {
          // Silent cleanup
        }
        delete currentConnections.current[connKey];
      }
    });

    const prevSelectionDetails = previousSelections.current[interactionKey];
    if (prevSelectionDetails?.merged) {
      cleanupOldMergeElements(
        instance,
        elementRefs.current,
        prevSelectionDetails.id,
        correspondingUserMessageIndex
      );
    }

    updateConnectionsForInteraction(
      instance,
      elementRefs.current,
      interactionKey,
      correspondingUserMessageIndex,
      aiResponseArrayIndex,
      currentSelectedResponse,
      aiResponses,
      connectionsByInteraction.current,
      themeValue,
      reorderedItemIds
    );

    setTimeout(() => {
      if (instance) {
        instance.repaintEverything();
      }
    }, 50);
  };

  const handleChangeOutput = useCallback(
    (element) => {
      const aiResponseIndex = messages.findIndex((msg) => {
        if (Array.isArray(msg)) {
          return msg.some((resp) => resp._id === element._id);
        }
        return false;
      });

      if (aiResponseIndex !== -1) {
        const correspondingUserMessageIndex = aiResponseIndex + 1;

        if (reorderedItems[correspondingUserMessageIndex]) {
          setReorderedItems((prev) => {
            const newReorderedItems = { ...prev };
            delete newReorderedItems[correspondingUserMessageIndex];
            return newReorderedItems;
          });

          setDotReorderSignal({
            userMessageIndex: correspondingUserMessageIndex,
            newOrder: null,
            timestamp: Date.now(),
          });
        }
      }

      changeOutput(element);
    },
    [changeOutput, messages, reorderedItems]
  );

  const handleRingClickFromUser = useCallback(
    (userMessageIndex, ringIndex) => {
      if (reorderedItems[userMessageIndex]) {
        setReorderedItems((prev) => {
          const newReorderedItems = { ...prev };
          delete newReorderedItems[userMessageIndex];
          return newReorderedItems;
        });

        setDotReorderSignal({
          userMessageIndex,
          newOrder: null,
          timestamp: Date.now(),
        });
      }

      setRingClickSignal({
        userMessageIndex,
        ringIndex,
        timestamp: Date.now(),
      });

      setTimeout(() => setRingClickSignal(null), 100);
    },
    [reorderedItems]
  );

  // Redraw entire tree when deletion occurs
  useEffect(() => {
    if (jsPlumbInstance.current) {
      // First cleanup all existing connections and elements
      performFullCleanup(jsPlumbInstance.current);

      // Then redraw everything
      updateConnections(jsPlumbInstance.current, elementRefs.current);
    }
  }, [redrawTrigger]);

  return (
    <>
      {selectionChangeWarning.open && (
        <SelectionChangeWarning
          open={selectionChangeWarning.open}
          onClose={handleCloseSelectionWarning}
          onConfirm={handleConfirmSelectionWarning}
        />
      )}
      {conversationPathWarning.open && (
        <ConversationPathWarning
          open={conversationPathWarning.open}
          onClose={handleCloseConversationPathWarning}
          onConfirm={handleConfirmConversationPathWarning}
        />
      )}
      <div
        ref={plumbContainerRef}
        className="relative w-full flex flex-col-reverse"
      >
        {messages?.map((item, index) => {
          const { realKey, isUserMessage } = processMessage(item);

          const mostRecentUserMessageIndex = messages?.findIndex((msg, idx) => {
            const { isUserMessage: isMsgUser } = processMessage(msg);
            return isMsgUser;
          });
          const isLatestUserMessage =
            isUserMessage && index === mostRecentUserMessageIndex;
          const isLatestAIResponseBlock =
            !isUserMessage && index === mostRecentUserMessageIndex - 1;

          let dotProps = {};
          if (isUserMessage && renderType === COLUMNS) {
            const aiResponsesIndex = index - 1;
            const subsequentResponses =
              getSubsequentResponses(aiResponsesIndex);

            if (subsequentResponses.length > 0) {
              const nonMergedSubsequentResponses = subsequentResponses.filter(
                (resp) => !resp.merged
              );

              const hasSpecificCapabilities = (responses) => {
                const specificCapabilities = [
                  '673e77c260fbf24164334533',
                  '6748ded2b344fda8a0338cc5',
                ];
                return responses.some((resp) =>
                  specificCapabilities.includes(
                    resp?.data?.function?.capability
                  )
                );
              };

              const selectedSubsequentResponse =
                subsequentResponses.find((resp) => resp.selected === true) ||
                (subsequentResponses.length === 1
                  ? subsequentResponses[0]
                  : null);

              let activeDotIndices = [];
              if (selectedSubsequentResponse) {
                if (selectedSubsequentResponse.merged) {
                  const sourceModels = Array.isArray(
                    selectedSubsequentResponse.model
                  )
                    ? selectedSubsequentResponse.model
                    : [selectedSubsequentResponse.model];
                  nonMergedSubsequentResponses.forEach((resp, idx) => {
                    if (sourceModels.includes(resp.model)) {
                      activeDotIndices.push(idx);
                    }
                  });
                } else {
                  const selectedIdx = nonMergedSubsequentResponses.findIndex(
                    (resp) => resp._id === selectedSubsequentResponse._id
                  );
                  if (selectedIdx !== -1) {
                    activeDotIndices.push(selectedIdx);
                  }
                }
              }

              dotProps = {
                subsequentResponseCount: nonMergedSubsequentResponses.length,
                selectedSubsequentResponse: selectedSubsequentResponse,
                activeSubsequentDotIndices: activeDotIndices,
                onDotClick: handleDotClick,
                messageIndexForDots: index,
                subsequentResponsesHaveSpecificCapabilities:
                  hasSpecificCapabilities(nonMergedSubsequentResponses),
              };
            }
          }

          return (
            <div key={`CHAT_MESSAGE_${index}`} className="mb-8">
              <ChatMessage
                key={realKey}
                item={item}
                index={index}
                avatar={avatar}
                chatId={xchatId}
                onCapturedScript={handleScriptForTTS}
                onDelete={handleMessageDelete}
                onDeleteResponse={handleResponseDelete}
                onOpenFull={handleOpenFull}
                changeOutput={handleChangeOutput}
                onRegenerate={onRegenerate}
                onRegenerateEdit={onRegenerateEdit}
                continueResponse={continueResponse}
                loading={loading}
                optimizing={optimizing}
                calculateCoinsEdit={calculateCoinsEdit}
                onAddModels={handleAddModels}
                onSend={onSend}
                onScrollRequest={handleScrollRequest}
                // New props for tree view
                {...dotProps}
                registerDotRef={registerElementRef}
                registerCardRef={registerElementRef}
                onCardReorder={handleCardReorder}
                onRingClickFromUser={
                  isUserMessage ? handleRingClickFromUser : undefined
                }
                ringClickSignal={!isUserMessage ? ringClickSignal : undefined}
                userMessageIndex={!isUserMessage ? index + 1 : undefined}
                isLatestUserMessage={isLatestUserMessage}
                isLatestAIResponseBlock={isLatestAIResponseBlock}
                dotReorderSignal={isUserMessage ? dotReorderSignal : undefined}
              />
              <ReferenceDiv
                item={item}
                index={index}
                realKey={realKey}
                messageRefs={messageRefs}
                isUserMessage={isUserMessage}
              />
            </div>
          );
        })}
      </div>
    </>
  );
};

const arePropsEqual = (prevProps, nextProps) => {
  if (
    prevProps.loading !== nextProps.loading ||
    prevProps.optimizing !== nextProps.optimizing ||
    prevProps.currentEvent !== nextProps.currentEvent ||
    prevProps.xchatId !== nextProps.xchatId
  ) {
    return false;
  }

  if (prevProps.messages?.length !== nextProps.messages?.length) {
    return false;
  }

  for (let i = 0; i < prevProps.messages.length; i++) {
    const prevItem = prevProps.messages[i];
    const nextItem = nextProps.messages[i];

    if (Array.isArray(prevItem) && Array.isArray(nextItem)) {
      if (prevItem.length !== nextItem.length) {
        return false;
      }

      for (let j = 0; j < prevItem.length; j++) {
        const prevResp = prevItem[j];
        const nextResp = nextItem[j];

        if (
          prevResp?._id !== nextResp?._id ||
          prevResp?.selected !== nextResp?.selected ||
          prevResp?.merged !== nextResp?.merged
        ) {
          return false;
        }
      }
    } else if (Array.isArray(prevItem) !== Array.isArray(nextItem)) {
      return false;
    } else {
      if (
        prevItem?._id !== nextItem?._id ||
        prevItem?.data?.content !== nextItem?.data?.content
      ) {
        return false;
      }
    }
  }

  return true;
};

export default memo(ListingChatMessages, arePropsEqual);
